/**
 * Running the project build as a subprocess.
 *
 * This is the IO half of the build check — the `project.ts` analogue for build:
 * it shells out to `corepack pnpm run build` in the project dir and captures the
 * combined output and exit code. The pure normalization into a `ValidationReport` lives in
 * `report.ts` (`buildReport`), so the report shape stays unit-testable without
 * spawning anything.
 *
 * A non-zero exit code is a *normal* result (a failed build, returned as a
 * `BuildOutput`) — only failing to spawn or wait on the process is a `UsageError`,
 * i.e. an infrastructure problem rather than a build failure.
 *
 * @module
 */
import { Effect, Stream } from "effect"
import { ChildProcess, ChildProcessSpawner } from "effect/unstable/process"
import { UsageError } from "./errors"
import type { BuildOutput } from "./report"

const BUILD_BIN = "corepack"
const BUILD_ARGS = ["pnpm", "run", "build"]

/** Cap captured output so `--json` payloads and fix text stay bounded — build logs can be large. */
const MAX_OUTPUT_CHARS = 20_000

const tail = (text: string, max: number): string =>
  text.length <= max ? text : `… (output truncated to last ${max} chars)\n${text.slice(-max)}`

/**
 * Run `corepack pnpm run build` in `projectDir`, capturing interleaved stdout+stderr and
 * the exit code. The output stream is drained to completion (process EOF) before
 * the exit code is read — the documented `ChildProcess` ordering, which also keeps
 * the OS pipe from blocking on a full buffer.
 */
export const runBuild = (
  projectDir: string,
): Effect.Effect<BuildOutput, UsageError, ChildProcessSpawner.ChildProcessSpawner> =>
  Effect.scoped(
    Effect.gen(function* () {
      const spawner = yield* ChildProcessSpawner.ChildProcessSpawner
      // extendEnv: inherit PATH so `pnpm` (and the node it invokes) resolve.
      const command = ChildProcess.make(BUILD_BIN, BUILD_ARGS, { cwd: projectDir, extendEnv: true })

      const handle = yield* spawner.spawn(command).pipe(
        Effect.mapError(
          (cause) =>
            new UsageError({
              reason: `Could not start the build (\`${BUILD_BIN} ${BUILD_ARGS.join(" ")}\`): ${cause.message}`,
            }),
        ),
      )

      const output = yield* Stream.mkString(handle.all.pipe(Stream.decodeText())).pipe(
        Effect.mapError(
          (cause) => new UsageError({ reason: `Could not read build output: ${cause.message}` }),
        ),
      )

      const exitCode = yield* handle.exitCode.pipe(
        Effect.mapError(
          (cause) => new UsageError({ reason: `The build process did not complete: ${cause.message}` }),
        ),
      )

      return {
        command: `${BUILD_BIN} ${BUILD_ARGS.join(" ")}`,
        exitCode,
        output: tail(output.trim(), MAX_OUTPUT_CHARS),
      }
    }),
  )
